import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CommonLayout from '../../layout/CommonLayout';
import { getMyPermissions } from '../../constants';
import {
  isSuperAdmin,
  setStoredPermissions,
} from '../../utils/permissions';

const TABS = [
  { id: 'workspace', label: 'Workspace Management', path: '/admin/workspaces', superAdminOnly: true },
  { id: 'project-access', label: 'Project Access', path: '/admin/project-access' },
  { id: 'roles', label: 'Roles & Permissions', path: '/admin/roles' },
];

const normalizeRole = (role) =>
  String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

function syncSessionRole(role) {
  if (!role) return;
  try {
    const raw = sessionStorage.getItem('userData');
    const parsed = raw ? JSON.parse(raw) : {};
    if (parsed.role !== role) {
      sessionStorage.setItem('userData', JSON.stringify({ ...parsed, role }));
    }
  } catch {
    /* ignore */
  }
}

export default function AdminLayout({ title, subtitle, children }) {
  const location = useLocation();
  const [showSuperAdminTabs, setShowSuperAdminTabs] = useState(() => isSuperAdmin());

  useEffect(() => {
    const onRoleUpdated = (event) => {
      const role = event?.detail?.role;
      const apiIsSa = normalizeRole(role) === 'super admin';
      // Never flicker SA tabs off from a partial/scoped role update
      setShowSuperAdminTabs((prev) => apiIsSa || isSuperAdmin() || prev);
    };
    window.addEventListener('qmetrix-role-updated', onRoleUpdated);
    return () => window.removeEventListener('qmetrix-role-updated', onRoleUpdated);
  }, []);

  // Record admin path so Standup can detect Admin → Standup and restore project/sprint defaults.
  // Set during render + effect so a quick Standup click after login still sees /admin (slow devices/dev).
  if (
    typeof window !== 'undefined' &&
    location.pathname.startsWith('/admin') &&
    sessionStorage.getItem('lastVisitedPath') !== location.pathname
  ) {
    sessionStorage.setItem('lastVisitedPath', location.pathname);
  }
  useEffect(() => {
    if (location.pathname.startsWith('/admin')) {
      sessionStorage.setItem('lastVisitedPath', location.pathname);
    }
  }, [location.pathname]);

  // Load once on mount — refetching on every admin tab change caused Workspace Management to flash hide/show
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const wasSuperAdmin = isSuperAdmin();
        const res = await getMyPermissions();
        if (cancelled) return;
        const payload = res?.data || res;
        const apiIsSa = normalizeRole(payload?.role) === 'super admin';

        // Do not overwrite Super Admin in session with a lesser company/project-scoped role
        if (payload?.role && (apiIsSa || !wasSuperAdmin)) {
          syncSessionRole(payload.role);
        }

        setShowSuperAdminTabs(apiIsSa || wasSuperAdmin || isSuperAdmin());
        setStoredPermissions(payload);
      } catch {
        if (!cancelled) setShowSuperAdminTabs(isSuperAdmin());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleTabs = TABS.filter((tab) => {
    if (!tab.superAdminOnly) return true;
    return showSuperAdminTabs;
  });

  // Project Access uses an internal split-pane scroll; other admin pages need page scroll
  const isProjectAccess = location.pathname === '/admin/project-access';

  return (
    <CommonLayout>
      <div className="flex flex-col w-full max-w-full min-w-0 h-[calc(100vh-60px)] max-h-[calc(100vh-60px)] overflow-hidden bg-[#F0F4F8] dark:bg-[#151F2C]">
        <div className="w-full px-[18px] pt-4 pb-2 shrink-0">
          <h1 className="font-bold text-2xl sm:text-3xl text-black dark:text-gray-100">{title}</h1>
          {subtitle && (
            <p className="mt-1 text-sm text-[#6B7C93] dark:text-[#8FA3B8]">{subtitle}</p>
          )}

          <nav className="flex gap-1 mt-3 border-b border-[#D1E2F0] dark:border-[#25384F] overflow-x-auto">
            {visibleTabs.map((tab) => {
              const isActive = location.pathname === tab.path;
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  className={`px-6 py-2.5 text-sm font-medium rounded-t-md border-b-2 -mb-px transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-[#326AEB] text-[#326AEB] dark:text-[#78B5FF] bg-white dark:bg-[#182433]'
                      : 'border-transparent text-[#6B7C93] dark:text-[#8FA3B8] hover:text-[#0A2342] dark:hover:text-white'
                  }`}
                >
                  {tab.label}
                </Link>
              );
            })}
          </nav>
        </div>
        <div
          className={`flex-1 min-h-0 flex flex-col ${
            isProjectAccess ? 'overflow-hidden' : 'overflow-y-auto'
          }`}
        >
          {children}
        </div>
      </div>
    </CommonLayout>
  );
}

AdminLayout.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
};

AdminLayout.defaultProps = {
  subtitle: '',
};
